import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { KeyRound, ArrowLeft } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 6) {
      setErr("Password must be at least 6 characters.");
      return;
    }
    if (pw !== pw2) {
      setErr("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      setTimeout(() => nav("/login"), 2200);
    } catch (e2) {
      setErr(e2.response?.data?.detail || e2.message || "Could not reset password.");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-16">
        <div className="card-bento max-w-md text-center" data-testid="reset-password-missing-token">
          <h1 className="text-2xl text-[#F7F5F0] mb-3" style={{ fontFamily: "Cormorant Garamond, serif" }}>
            Reset link is incomplete.
          </h1>
          <p className="text-sm text-[#8A8782] mb-6">
            This page needs a token from the email we sent. Start over from the forgot-password screen.
          </p>
          <Link to="/forgot-password" className="btn-primary inline-block">Request a new link</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-16 grain relative" data-testid="reset-password-page">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(201,169,97,0.08),_transparent_60%)]" />
      <div className="relative card-bento w-full max-w-md">
        <div className="overline mb-3">Set New Password</div>
        <h1
          className="text-3xl md:text-4xl text-[#F7F5F0] mb-2"
          style={{ fontFamily: "Cormorant Garamond, serif" }}
        >
          {done ? "All set." : "Choose a new password."}
        </h1>
        <p className="text-sm text-[#8A8782] mb-8">
          {done
            ? "Your password has been updated. Redirecting you to sign in…"
            : "Pick something you'll remember. Minimum 6 characters."}
        </p>

        {!done && (
          <form onSubmit={submit} className="space-y-5" data-testid="reset-password-form">
            <div>
              <label className="block text-sm text-[#D1CFC9] mb-2 flex items-center gap-1.5">
                <KeyRound className="w-3.5 h-3.5 text-[#C9A961]" /> New password
              </label>
              <input
                type="password"
                required
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                className="input-tcpi"
                placeholder="••••••••"
                data-testid="reset-pw-input"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-sm text-[#D1CFC9] mb-2">Confirm password</label>
              <input
                type="password"
                required
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                className="input-tcpi"
                placeholder="••••••••"
                data-testid="reset-pw2-input"
              />
            </div>

            {err && (
              <div className="text-sm text-red-400" data-testid="reset-error">{err}</div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full disabled:opacity-60"
              data-testid="reset-submit-btn"
            >
              {loading ? "Saving…" : "Save new password"}
            </button>
          </form>
        )}

        <div className="mt-6 text-sm text-center">
          <Link to="/login" className="text-[#C9A961] hover:underline inline-flex items-center gap-1.5" data-testid="reset-back-link">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
